/**
 * ShipmentReceiptForm.tsx
 *
 * "Verify Receipt by SKU" form shown to the ticket's vendor and to Sustain Team members once the
 * shipment has tracking. Verification is per SKU and per unit; a variance flags the shipment
 * instead of reconciling it and always needs a written explanation.
 *
 * Design source: Ticketing Portal Repair / Replace / Return mockup, Shipping and Vendor Receipt card.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import BarcodeScannerSimple from "../../../components/BarcodeScannerSimple";
import {
  ReceiptAction,
  ReceiptBlock,
  ReceiptCondition,
  ReceiptSku,
  UnitDisposition,
  submitReceipt,
} from "../../../api/shipmentApi";
import {
  CONDITION_OPTIONS,
  DISPOSITION_OPTIONS,
  NOTES_MAX_LENGTH,
  RECEIPT_MESSAGES,
  ReceiptDraft,
  buildInitialDraft,
  buildReceiptRequest,
  computeVariances,
  countUnverified,
  findUnitBySerial,
  newClientRequestId,
  normalizeSerial,
  parseQuantity,
  reconcileDraft,
  validateSubmit,
} from "../util/shipmentReceiptUtils";
import { clearReceiptDraft, loadReceiptDraft, saveReceiptDraft } from "../util/shipmentReceiptDraftStorage";

export interface ShipmentReceiptFormProps {
  ticketId: number;
  shipmentId: number;
  receipt: ReceiptBlock;
  /** Called after the backend accepted a confirmation or flag, with the backend's message. */
  onSubmitted: (message: string) => void;
}

const MONO = Platform.OS === "ios" ? "Courier" : "monospace";

// ---------------------------------------------------------------------------
// Disposition selector: native <select> on web, touch-sized segments on mobile
// ---------------------------------------------------------------------------

const DispositionSelect: React.FC<{
  value: UnitDisposition;
  onChange: (v: UnitDisposition) => void;
  label: string;
  disabled?: boolean;
}> = ({ value, onChange, label, disabled }) => {
  if (Platform.OS === "web") {
    return React.createElement(
      "select",
      {
        value,
        disabled,
        "aria-label": label,
        onChange: (ev: any) => onChange(ev.target.value as UnitDisposition),
        style: {
          flex: 1,
          minWidth: 0,
          height: 30,
          fontSize: 12,
          color: "#111827",
          border: "1px solid #9ca3af",
          borderRadius: 4,
          backgroundColor: "#ffffff",
          paddingLeft: 4,
        },
      },
      DISPOSITION_OPTIONS.map((o) => React.createElement("option", { key: o.value, value: o.value }, o.label))
    );
  }
  return (
    <View style={s.segRow}>
      {DISPOSITION_OPTIONS.map((o) => {
        const active = o.value === value;
        return (
          <Pressable
            key={o.value}
            disabled={disabled}
            onPress={() => onChange(o.value)}
            accessibilityRole="radio"
            accessibilityState={{ selected: active }}
            accessibilityLabel={`${label}: ${o.label}`}
            style={[
              s.seg,
              active && o.value === "RECEIVED" && s.segReceived,
              active && o.value === "DAMAGED" && s.segDamaged,
              active && o.value === "NOT_IN_SHIPMENT" && s.segMissing,
            ]}
          >
            <Text style={[s.segText, active && s.segTextActive]}>{o.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

const ShipmentReceiptForm: React.FC<ShipmentReceiptFormProps> = ({ ticketId, shipmentId, receipt, onSubmitted }) => {
  const skus: ReceiptSku[] = useMemo(() => receipt.skus ?? [], [receipt.skus]);
  const { width } = useWindowDimensions();
  const isNarrow = Platform.OS !== "web" || width < 720;

  const [draft, setDraft] = useState<ReceiptDraft>(() => buildInitialDraft(skus));
  const hydrated = useRef(false);
  const notesRef = useRef<TextInput>(null);
  const [submitting, setSubmitting] = useState<ReceiptAction | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [failedAction, setFailedAction] = useState<ReceiptAction | null>(null);
  const [notesInvalid, setNotesInvalid] = useState(false);
  const [scanText, setScanText] = useState("");
  const [scanFeedback, setScanFeedback] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  // Restore a partially completed count saved on this device.
  useEffect(() => {
    let active = true;
    hydrated.current = false;
    loadReceiptDraft(ticketId, shipmentId).then((stored) => {
      if (!active) return;
      if (stored) setDraft(reconcileDraft(skus, stored));
      hydrated.current = true;
    });
    return () => { active = false; };
    // Only the shipment identity decides whether a stored draft applies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, shipmentId]);

  // Keep the count on the device so a dropped connection never loses it.
  useEffect(() => {
    if (!hydrated.current) return;
    const t = setTimeout(() => {
      if (hydrated.current) saveReceiptDraft(ticketId, shipmentId, draft);
    }, 300);
    return () => clearTimeout(t);
  }, [draft, ticketId, shipmentId]);

  const variances = useMemo(() => computeVariances(skus, draft), [skus, draft]);
  const verifiedCount = skus.length - countUnverified(skus, draft);
  const hasVariance = variances.length > 0;

  const patch = (changes: Partial<ReceiptDraft>) => { setDraft((d) => ({ ...d, ...changes })); setFormError(null); setFailedAction(null); };
  const setVerified = (sku: string, on: boolean) => patch({ verified: { ...draft.verified, [sku]: on } });
  const setDisposition = (lineId: number, v: UnitDisposition) =>
    patch({ dispositions: { ...draft.dispositions, [String(lineId)]: v } });
  const setQuantity = (sku: string, text: string) =>
    patch({ quantities: { ...draft.quantities, [sku]: text.replace(/[^0-9]/g, "").slice(0, 6) } });
  const setCondition = (c: ReceiptCondition) => patch({ condition: c });
  const setNotes = (text: string) => { patch({ notes: text.slice(0, NOTES_MAX_LENGTH) }); setNotesInvalid(false); };

  const applyScan = (raw: string) => {
    const serial = normalizeSerial((raw ?? "").replace(/[\x00-\x1f\x7f]/g, "")).slice(0, 64);
    if (!serial) return;
    const match = findUnitBySerial(skus, serial);
    if (match) {
      patch({ dispositions: { ...draft.dispositions, [String(match.lineId)]: "RECEIVED" } });
      setScanFeedback(`${match.serialNumber} marked Received.`);
    } else if (draft.unexpectedSerials.includes(serial)) {
      setScanFeedback(`${serial} is already listed as an unexpected item.`);
    } else {
      patch({ unexpectedSerials: [...draft.unexpectedSerials, serial] });
      setScanFeedback(`${serial} is not on this shipment. It was added as an unexpected item.`);
    }
    setScanText("");
  };

  const removeUnexpected = (serial: string) =>
    patch({ unexpectedSerials: draft.unexpectedSerials.filter((x) => x !== serial) });

  const submit = async (action: ReceiptAction) => {
    if (submitting) return;
    setFormError(null);
    setFailedAction(null);
    const check = validateSubmit(action, skus, draft);
    if (!check.ok) {
      setFormError(check.message);
      if (check.field === "notes") {
        setNotesInvalid(true);
        notesRef.current?.focus();
      }
      return;
    }
    setNotesInvalid(false);

    const requestId = draft.requestIds[action] ?? newClientRequestId();
    if (!draft.requestIds[action]) setDraft((d) => ({ ...d, requestIds: { ...d.requestIds, [action]: requestId } }));

    setSubmitting(action);
    const result = await submitReceipt(ticketId, buildReceiptRequest(action, skus, draft, requestId));
    setSubmitting(null);

    if (result.error || !result.data) {
      if (result.retryable) {
        setFormError(RECEIPT_MESSAGES.confirmFailedRetry);
        setFailedAction(action);
      } else {
        setFormError(result.error ?? "Failed to submit the receipt.");
      }
      return;
    }

    if (action === "CONFIRM") {
      hydrated.current = false;
      await clearReceiptDraft(ticketId, shipmentId);
    } else {
      // A flag leaves the form open; the later confirmation needs its own request id.
      setDraft((d) => ({ ...d, requestIds: { ...d.requestIds, FLAG: undefined } }));
    }
    onSubmitted(result.data.message);
  };

  const busy = submitting !== null;

  return (
    <View style={s.wrap}>
      <View style={s.head}>
        <View style={s.headLeft}>
          <MaterialIcons name="fact-check" size={15} color="#034895" />
          <Text style={s.headTitle}>Verify Receipt by SKU</Text>
        </View>
        {receipt.actingAs ? <Text style={s.acting}>{receipt.actingAs}</Text> : null}
      </View>

      <View style={s.progress}>
        <Text style={s.progressText}>{verifiedCount} of {skus.length} SKU(s) verified</Text>
      </View>

      <View style={s.scanRow}>
        <TextInput
          style={s.scanInput}
          value={scanText}
          onChangeText={setScanText}
          onSubmitEditing={() => applyScan(scanText)}
          placeholder="Scan or enter a serial number"
          placeholderTextColor="#9ca3af"
          autoCapitalize="characters"
          autoCorrect={false}
          editable={!busy}
          accessibilityLabel="Scan or enter a serial number"
        />
        <Pressable
          style={({ pressed }) => [s.scanBtn, pressed && s.pressed]}
          onPress={() => applyScan(scanText)}
          disabled={busy || !scanText.trim()}
        >
          <Text style={s.scanBtnText}>Mark</Text>
        </Pressable>
        {Platform.OS !== "web" ? (
          <Pressable
            style={({ pressed }) => [s.scanCamBtn, pressed && s.pressed]}
            onPress={() => setScannerOpen(true)}
            disabled={busy}
            accessibilityLabel="Scan a serial number with the camera"
          >
            <MaterialIcons name="qr-code-scanner" size={20} color="#034895" />
          </Pressable>
        ) : null}
      </View>
      {scanFeedback ? <Text style={s.scanFeedback}>{scanFeedback}</Text> : null}
      {draft.unexpectedSerials.length > 0 ? (
        <View style={s.chipsRow}>
          <Text style={s.chipsLabel}>Unexpected items:</Text>
          {draft.unexpectedSerials.map((serial) => (
            <Pressable key={serial} style={s.unexpectedChip} onPress={() => removeUnexpected(serial)} disabled={busy}>
              <Text style={s.unexpectedChipText}>{serial}</Text>
              <MaterialIcons name="close" size={12} color="#B42318" />
            </Pressable>
          ))}
        </View>
      ) : null}

      {skus.map((sku) => {
        const verified = !!draft.verified[sku.sku];
        const isSerial = sku.captureMode === "SERIAL";
        const qtyText = draft.quantities[sku.sku] ?? "";
        const qtyInvalid = !isSerial && parseQuantity(qtyText) === null;
        return (
          <View key={sku.sku} style={[s.skuBlock, verified && s.skuBlockVerified]}>
            <View style={[s.skuHead, isNarrow && s.skuHeadNarrow]}>
              <View style={s.skuHeadLeft}>
                <Text style={s.skuDesc}>{sku.description}</Text>
                <View style={s.capPill}><Text style={s.capPillText}>{isSerial ? "SERIAL" : "QTY"}</Text></View>
                <Text style={s.skuMeta}>{sku.sku} · {sku.expectedQuantity} expected</Text>
              </View>
              <Pressable
                style={s.verifyRow}
                onPress={() => setVerified(sku.sku, !verified)}
                disabled={busy}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: verified }}
                accessibilityLabel={`SKU verified ${sku.sku}`}
              >
                <MaterialIcons name={verified ? "check-box" : "check-box-outline-blank"} size={20} color={verified ? "#034895" : "#6b7280"} />
                <Text style={s.verifyText}>SKU verified</Text>
              </Pressable>
            </View>

            {isSerial ? (
              <View style={s.unitGrid}>
                {(sku.units ?? []).map((u) => {
                  const d = draft.dispositions[String(u.lineId)] ?? "RECEIVED";
                  return (
                    <View
                      key={u.lineId}
                      style={[
                        s.unitCell,
                        isNarrow ? s.unitCellNarrow : s.unitCellWide,
                        d === "DAMAGED" && s.unitCellDamaged,
                        d === "NOT_IN_SHIPMENT" && s.unitCellMissing,
                      ]}
                    >
                      <Text style={s.serial} numberOfLines={1}>{u.serialNumber}</Text>
                      <DispositionSelect
                        value={d}
                        onChange={(v) => setDisposition(u.lineId, v)}
                        label={`Disposition for ${u.serialNumber}`}
                        disabled={busy}
                      />
                    </View>
                  );
                })}
              </View>
            ) : (
              <View style={s.qtyRow}>
                <Text style={s.qtyLabel}>Expected {sku.expectedQuantity}</Text>
                <Text style={s.qtyLabel}>Received</Text>
                <TextInput
                  style={[s.qtyInput, qtyInvalid && s.inputError]}
                  value={qtyText}
                  onChangeText={(t) => setQuantity(sku.sku, t)}
                  keyboardType="number-pad"
                  inputMode="numeric"
                  editable={!busy}
                  accessibilityLabel={`Received quantity for ${sku.sku}`}
                />
                {qtyInvalid ? <Text style={s.fieldError}>{RECEIPT_MESSAGES.quantityInvalid}</Text> : null}
              </View>
            )}
          </View>
        );
      })}

      {hasVariance ? (
        <View style={[s.summary, s.summaryBad]}>
          <MaterialIcons name="error-outline" size={18} color="#B42318" style={s.summaryIcon} />
          <View style={{ flex: 1 }}>
            <Text style={s.summaryTitle}>{variances.length} variance(s) against the shipment record</Text>
            {variances.map((v) => (
              <Text key={v.key} style={s.summaryLine}>
                {v.sku ? <Text style={s.summarySku}>{v.sku}: </Text> : null}
                {v.detail}
              </Text>
            ))}
            <Text style={s.summaryStatement}>{RECEIPT_MESSAGES.varianceStatement}</Text>
          </View>
        </View>
      ) : (
        <View style={[s.summary, s.summaryOk]}>
          <MaterialIcons name="check" size={18} color="#027A48" style={s.summaryIcon} />
          <Text style={s.summaryOkText}>{RECEIPT_MESSAGES.noVariance}</Text>
        </View>
      )}

      <Text style={s.fieldLabel}>Condition on receipt</Text>
      <View style={s.condRow}>
        {CONDITION_OPTIONS.map((c) => {
          const active = draft.condition === c;
          return (
            <Pressable
              key={c}
              style={[s.condChip, active && s.condChipActive]}
              onPress={() => setCondition(c)}
              disabled={busy}
              accessibilityRole="radio"
              accessibilityState={{ selected: active }}
            >
              <Text style={[s.condChipText, active && s.condChipTextActive]}>{c}</Text>
            </Pressable>
          );
        })}
      </View>

      <Text style={[s.fieldLabel, { marginTop: 12 }]}>
        Variance detail{" "}
        {hasVariance ? <Text style={s.required}>*</Text> : <Text style={s.optional}>optional</Text>}
      </Text>
      <TextInput
        ref={notesRef}
        style={[s.notesInput, notesInvalid && s.inputError]}
        value={draft.notes}
        onChangeText={setNotes}
        placeholder={RECEIPT_MESSAGES.notesPlaceholder}
        placeholderTextColor="#9ca3af"
        multiline
        numberOfLines={3}
        maxLength={NOTES_MAX_LENGTH}
        editable={!busy}
        accessibilityLabel="Variance detail"
      />
      <Text style={s.helper}>{RECEIPT_MESSAGES.notesGuidance}</Text>

      {formError ? (
        <View style={s.errorBox}>
          <MaterialIcons name="error-outline" size={16} color="#B42318" />
          <Text style={s.errorText}>{formError}</Text>
          {failedAction ? (
            <Pressable style={({ pressed }) => [s.retryBtn, pressed && s.pressed]} onPress={() => submit(failedAction)} disabled={busy}>
              <Text style={s.retryText}>Retry</Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      <View style={s.actions}>
        <Pressable
          style={({ pressed }) => [s.btnOutline, pressed && s.pressed, busy && s.disabled]}
          onPress={() => submit("FLAG")}
          disabled={busy}
        >
          {submitting === "FLAG" ? <ActivityIndicator size="small" color="#034895" /> : <Text style={s.btnOutlineText}>Flag Variance</Text>}
        </Pressable>
        <Pressable
          style={({ pressed }) => [s.btnPrimary, pressed && s.pressed, busy && s.disabled]}
          onPress={() => submit("CONFIRM")}
          disabled={busy}
        >
          {submitting === "CONFIRM" ? <ActivityIndicator size="small" color="#ffffff" /> : <Text style={s.btnPrimaryText}>Confirm Received</Text>}
        </Pressable>
      </View>

      {Platform.OS !== "web" ? (
        <BarcodeScannerSimple
          visible={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onBarcodeScanned={(code: string) => applyScan(code)}
          expectedLength={0}
          title="Scan serial number"
        />
      ) : null}
    </View>
  );
};

const s = StyleSheet.create({
  wrap: { paddingHorizontal: 14, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#e5e7eb", backgroundColor: "#ffffff" },
  head: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: 10 },
  headLeft: { flexDirection: "row", alignItems: "center", gap: 6 },
  headTitle: { fontSize: 13, fontWeight: "700", color: "#034895" },
  acting: { fontSize: 11, color: "#6b7280" },
  progress: { backgroundColor: "#EEF4FB", borderRadius: 6, paddingHorizontal: 12, paddingVertical: 9, marginBottom: 10 },
  progressText: { fontSize: 12, fontWeight: "700", color: "#034895" },
  scanRow: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 },
  scanInput: { flex: 1, height: 36, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, paddingHorizontal: 10, fontSize: 13, color: "#2D2D2D", backgroundColor: "#ffffff" },
  scanBtn: { height: 36, paddingHorizontal: 14, borderRadius: 6, borderWidth: 1, borderColor: "#034895", alignItems: "center", justifyContent: "center", backgroundColor: "#ffffff" },
  scanBtnText: { fontSize: 12, fontWeight: "700", color: "#034895" },
  scanCamBtn: { width: 38, height: 36, borderRadius: 6, borderWidth: 1, borderColor: "#034895", alignItems: "center", justifyContent: "center", backgroundColor: "#ffffff" },
  scanFeedback: { fontSize: 12, color: "#374151", marginBottom: 6 },
  chipsRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 8 },
  chipsLabel: { fontSize: 12, fontWeight: "600", color: "#B42318" },
  unexpectedChip: { flexDirection: "row", alignItems: "center", gap: 4, backgroundColor: "#FEF3F2", borderWidth: 1, borderColor: "#FDA29B", borderRadius: 100, paddingHorizontal: 8, paddingVertical: 3 },
  unexpectedChipText: { fontSize: 11, color: "#B42318", fontFamily: MONO },
  skuBlock: { borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 12, marginBottom: 10, backgroundColor: "#ffffff" },
  skuBlockVerified: { borderColor: "#86d99a", borderLeftWidth: 3, borderLeftColor: "#16a34a", backgroundColor: "#F3FBF5" },
  skuHead: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 },
  skuHeadNarrow: { flexDirection: "column", alignItems: "flex-start" },
  skuHeadLeft: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, flexShrink: 1 },
  skuDesc: { fontSize: 13, fontWeight: "700", color: "#111827" },
  capPill: { backgroundColor: "#DAEEFA", borderRadius: 4, paddingHorizontal: 6, paddingVertical: 2 },
  capPillText: { fontSize: 9, fontWeight: "700", color: "#005CBC", letterSpacing: 0.5 },
  skuMeta: { fontSize: 11, color: "#6b7280", fontFamily: MONO },
  verifyRow: { flexDirection: "row", alignItems: "center", gap: 6, paddingVertical: 4 },
  verifyText: { fontSize: 12, fontWeight: "600", color: "#374151" },
  unitGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  unitCell: { flexDirection: "row", alignItems: "center", gap: 8, borderWidth: 1, borderColor: "#86d99a", backgroundColor: "#F3FBF5", borderRadius: 6, paddingHorizontal: 8, paddingVertical: 6 },
  unitCellWide: { width: "24%", minWidth: 200, flexGrow: 1 },
  unitCellNarrow: { width: "100%", flexDirection: "column", alignItems: "stretch", gap: 6, paddingVertical: 8 },
  unitCellDamaged: { borderColor: "#FDA29B", backgroundColor: "#FEF3F2" },
  unitCellMissing: { borderColor: "#FEC84B", backgroundColor: "#FFFAEB" },
  serial: { fontSize: 12, color: "#374151", fontFamily: MONO },
  segRow: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  seg: { flexGrow: 1, minHeight: 40, alignItems: "center", justifyContent: "center", borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, backgroundColor: "#ffffff", paddingHorizontal: 8 },
  segReceived: { borderColor: "#16a34a", backgroundColor: "#DDF9DA" },
  segDamaged: { borderColor: "#B42318", backgroundColor: "#FEE4E2" },
  segMissing: { borderColor: "#B54708", backgroundColor: "#FEF0C7" },
  segText: { fontSize: 12, fontWeight: "600", color: "#374151", textAlign: "center" },
  segTextActive: { color: "#111827" },
  qtyRow: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 10 },
  qtyLabel: { fontSize: 12, color: "#374151", fontWeight: "600" },
  qtyInput: { width: 90, height: 36, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, paddingHorizontal: 10, fontSize: 13, color: "#2D2D2D", backgroundColor: "#ffffff" },
  fieldError: { fontSize: 11, color: "#B42318", width: "100%" },
  summary: { flexDirection: "row", gap: 8, borderRadius: 8, borderWidth: 1, padding: 12, marginBottom: 12, alignItems: "flex-start" },
  summaryIcon: { marginTop: 1 },
  summaryOk: { backgroundColor: "#ECFDF3", borderColor: "#ABEFC6", alignItems: "center" },
  summaryOkText: { flex: 1, fontSize: 12, fontWeight: "700", color: "#027A48" },
  summaryBad: { backgroundColor: "#FEF3F2", borderColor: "#FDA29B" },
  summaryTitle: { fontSize: 12, fontWeight: "700", color: "#B42318", marginBottom: 4 },
  summaryLine: { fontSize: 12, color: "#7A271A", lineHeight: 18 },
  summarySku: { fontWeight: "700", fontFamily: MONO },
  summaryStatement: { fontSize: 11, color: "#7A271A", marginTop: 6, lineHeight: 16 },
  fieldLabel: { fontSize: 12, fontWeight: "600", color: "#374151", marginBottom: 6 },
  optional: { fontSize: 11, fontWeight: "400", color: "#9ca3af" },
  required: { fontSize: 12, fontWeight: "700", color: "#B42318" },
  condRow: { flexDirection: "row", gap: 8, flexWrap: "wrap" },
  condChip: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, borderWidth: 1, borderColor: "#D0D0D0", backgroundColor: "#ffffff", minHeight: 36, justifyContent: "center" },
  condChipActive: { borderColor: "#034895", backgroundColor: "#E6EEF8" },
  condChipText: { fontSize: 13, fontWeight: "600", color: "#374151" },
  condChipTextActive: { color: "#034895" },
  notesInput: { minHeight: 76, borderWidth: 1, borderColor: "#D0D0D0", borderRadius: 6, padding: 10, fontSize: 13, color: "#2D2D2D", backgroundColor: "#ffffff", textAlignVertical: "top" },
  inputError: { borderColor: "#B42318", borderWidth: 1.5 },
  helper: { fontSize: 11, color: "#6b7280", marginTop: 6, lineHeight: 16 },
  errorBox: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 12, backgroundColor: "#FEF3F2", borderWidth: 1, borderColor: "#FDA29B", borderRadius: 8, padding: 10 },
  errorText: { flex: 1, fontSize: 12, color: "#B42318", lineHeight: 17, minWidth: 180 },
  retryBtn: { borderWidth: 1, borderColor: "#B42318", borderRadius: 6, paddingHorizontal: 12, paddingVertical: 6, backgroundColor: "#ffffff" },
  retryText: { fontSize: 12, fontWeight: "700", color: "#B42318" },
  actions: { flexDirection: "row", justifyContent: "flex-end", flexWrap: "wrap", gap: 10, marginTop: 14 },
  btnOutline: { minHeight: 38, minWidth: 120, borderWidth: 1.5, borderColor: "#034895", borderRadius: 100, paddingHorizontal: 16, alignItems: "center", justifyContent: "center", backgroundColor: "#ffffff" },
  btnOutlineText: { fontSize: 13, fontWeight: "700", color: "#034895" },
  btnPrimary: { minHeight: 38, minWidth: 140, borderRadius: 100, paddingHorizontal: 18, alignItems: "center", justifyContent: "center", backgroundColor: "#034895" },
  btnPrimaryText: { fontSize: 13, fontWeight: "700", color: "#ffffff" },
  pressed: { opacity: 0.8 },
  disabled: { opacity: 0.55 },
});

export default ShipmentReceiptForm;
